import React, { useCallback, useEffect, useState } from 'react';
import { Award, Crown, RefreshCw, Save } from 'lucide-react';
import { apiCall } from './_shared';

const TierForm = ({ id, tier, onChange, canEdit }) => {
  const Icon = id === 'gold' ? Crown : Award;
  return (
    <div className={`rounded-lg border p-3 ${id === 'gold' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`} data-testid={`loyalty-tier-${id}`}>
      <p className="text-xs font-bold uppercase inline-flex items-center gap-1 mb-2"><Icon className="w-4 h-4" /> {id}</p>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <label>stays ≥ <input type="number" min={1} value={tier.min_stays} onChange={(e) => onChange({ ...tier, min_stays: Number(e.target.value) })} disabled={!canEdit} className="border rounded px-1 py-0.5 w-14" data-testid={`loyalty-${id}-stays`} /></label>
        <label>or nights ≥ <input type="number" min={1} value={tier.min_nights} onChange={(e) => onChange({ ...tier, min_nights: Number(e.target.value) })} disabled={!canEdit} className="border rounded px-1 py-0.5 w-14" data-testid={`loyalty-${id}-nights`} /></label>
        <label>discount <input type="number" min={0} max={50} value={tier.discount_pct} onChange={(e) => onChange({ ...tier, discount_pct: Number(e.target.value) })} disabled={!canEdit} className="border rounded px-1 py-0.5 w-14" data-testid={`loyalty-${id}-pct`} /> %</label>
      </div>
      <textarea value={(tier.benefits || []).join('\n')} onChange={(e) => onChange({ ...tier, benefits: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean).slice(0, 8) })} disabled={!canEdit} rows={4} className="mt-2 w-full border rounded px-2 py-1 text-xs" placeholder="one benefit per line" data-testid={`loyalty-${id}-benefits`} />
    </div>
  );
};

const MemberRows = ({ members }) => {
  const list = members || [];
  const rows = list.map((m) => (
    <tr key={m.ident} className="border-t" data-testid={`loyalty-member-${m.ident}`}>
      <td className="py-1 pr-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${m.tier === 'gold' ? 'bg-amber-100 text-amber-800' : m.tier === 'silver' ? 'bg-slate-200 text-slate-800' : 'bg-white border text-slate-500'}`}>{m.tier}</span></td>
      <td className="py-1 pr-2 font-semibold text-slate-900">{m.guest_name || '—'}<br /><span className="text-[11px] font-normal text-slate-500">{m.email || m.phone || ''}{m.country ? ` · ${m.country}` : ''}</span></td>
      <td className="py-1 pr-2 text-right">{m.stays}</td>
      <td className="py-1 pr-2 text-right">{m.nights}</td>
      <td className="py-1 pr-2 text-right">€{Number(m.spend).toFixed(0)}</td>
      <td className="py-1 pr-2 text-right text-slate-600">{m.first_stay} → {m.last_stay}</td>
    </tr>
  ));
  return <tbody>{rows}</tbody>;
};

export const LoyaltyCard = ({ userRole }) => {
  const canEdit = ['super_admin', 'admin', 'manager'].includes(userRole);
  const [cfg, setCfg] = useState(null);
  const [data, setData] = useState(null);
  const [tier, setTier] = useState('');
  const [q, setQ] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => {
    try {
      const [c, m] = await Promise.all([apiCall('/api/admin/loyalty/settings'), apiCall(`/api/admin/loyalty/members?limit=100${tier ? `&tier=${tier}` : ''}${q ? `&q=${encodeURIComponent(q)}` : ''}`)]);
      setCfg(c); setData(m);
    } catch (e) { setMsg(e.message); }
  }, [tier, q]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try { await apiCall('/api/admin/loyalty/settings', { method: 'PUT', body: JSON.stringify({ enabled: cfg.enabled, silver: cfg.silver, gold: cfg.gold, coupon_days: Number(cfg.coupon_days) }) }); setMsg('Saved — tiers recalculated.'); await load(); } catch (e) { setMsg(e.message); }
  };
  const counts = data?.counts;
  return (
    <div className="bg-white rounded-xl border shadow-sm p-4" data-testid="loyalty-card">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <h3 className="font-bold text-slate-900 inline-flex items-center gap-2"><Crown className="w-5 h-5 text-amber-500" /> Loyalty tiers · Silver &amp; Gold</h3>
        <span className="text-xs text-slate-500">Status is computed from completed stays in the Beds24 archive. Guests check it on the booking page (e-mail or phone + surname) and get a personal direct-booking code.</span>
        <button type="button" onClick={load} className="ml-auto p-1.5 border rounded hover:bg-slate-50" title="Refresh" data-testid="loyalty-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
      </div>
      {counts && (
        <div className="flex flex-wrap gap-3 text-sm mb-3" data-testid="loyalty-counts">
          <span className="px-2 py-1 rounded bg-amber-100 text-amber-900 font-semibold">Gold {counts.gold}</span>
          <span className="px-2 py-1 rounded bg-slate-200 text-slate-900 font-semibold">Silver {counts.silver}</span>
          <span className="px-2 py-1 rounded border text-slate-600">Other guests {counts.none}</span>
          <span className="px-2 py-1 text-slate-500">{data.status_checks} status check(s) on the booking page</span>
        </div>
      )}
      {cfg && (
        <div className="grid md:grid-cols-2 gap-3">
          <TierForm id="silver" tier={cfg.silver} onChange={(t) => setCfg({ ...cfg, silver: t })} canEdit={canEdit} />
          <TierForm id="gold" tier={cfg.gold} onChange={(t) => setCfg({ ...cfg, gold: t })} canEdit={canEdit} />
        </div>
      )}
      {cfg && (
        <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
          <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} disabled={!canEdit} data-testid="loyalty-enabled" /> show status check on the booking page</label>
          <label>code valid <input type="number" min={7} max={730} value={cfg.coupon_days} onChange={(e) => setCfg({ ...cfg, coupon_days: e.target.value })} disabled={!canEdit} className="border rounded px-1 py-0.5 w-16" data-testid="loyalty-coupon-days" /> days</label>
          {canEdit && <button type="button" onClick={save} className="inline-flex items-center gap-1 bg-slate-900 text-white rounded px-3 py-1.5 font-semibold" data-testid="loyalty-save"><Save className="w-3.5 h-3.5" /> Save</button>}
          {msg && <span className="text-slate-600" data-testid="loyalty-msg">{msg}</span>}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-4">
        <select value={tier} onChange={(e) => setTier(e.target.value)} className="border rounded px-2 py-1 text-xs" data-testid="loyalty-filter-tier"><option value="">All tiers</option><option value="gold">Gold</option><option value="silver">Silver</option><option value="none">Other</option></select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search name / e-mail / country" className="border rounded px-2 py-1 text-xs w-60" data-testid="loyalty-search" />
        {data && <span className="text-xs text-slate-500" data-testid="loyalty-total">{data.total} guest(s)</span>}
      </div>
      {data && (
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-xs" data-testid="loyalty-members">
            <thead><tr className="text-[10px] uppercase text-slate-500 text-left"><th className="pb-1 pr-2">Tier</th><th className="pb-1 pr-2">Guest</th><th className="pb-1 pr-2 text-right">Stays</th><th className="pb-1 pr-2 text-right">Nights</th><th className="pb-1 pr-2 text-right">Spend</th><th className="pb-1 pr-2 text-right">First → last</th></tr></thead>
            <MemberRows members={data.members} />
          </table>
        </div>
      )}
    </div>
  );
};

export default LoyaltyCard;
